import { Head } from "@inertiajs/react"
import { Eye } from "@untitledui/icons"
import { format } from "date-fns"

import { PageHeader } from "@/components/console/page-header"
import { Badge, StatusBadge, type StatusTier } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { MessageItem } from "@/components/ui/messaging"
import { SlideoutMenu } from "@/components/ui/slideout-menu"
import { AdminLayout } from "@/layouts/admin-layout"
import { adminMsgFlowRunsPath } from "@/routes"

import {
  type BubbleSourceLine,
  decorateBubbles,
} from "./_components/decorate-bubble"
import { type InspectorData, RunInspector } from "./_components/run-inspector"

type RunStatus =
  | "pending"
  | "active"
  | "paused"
  | "closed_listening"
  | "completed"
  | "terminated"

interface Run {
  id: number
  contact: {
    name: string
    first_name: string
    phone: string | null
    time_zone: string | null
    initials: string
    account: string | null
  }
  definition: { code: string; label: string; version: number }
  status: RunStatus
  enrolled_at: string | null
  closed_listening_at: string | null
}

interface TranscriptLine extends BubbleSourceLine {
  status: string | null
}

interface Props {
  run: Run
  transcript: TranscriptLine[]
  inspector: InspectorData
}

const STATUS_LABEL: Record<RunStatus, string> = {
  pending: "Pending",
  active: "Active",
  paused: "Paused",
  closed_listening: "Closed listening",
  completed: "Completed",
  terminated: "Terminated",
}

const STATUS_COLOR: Record<RunStatus, StatusTier> = {
  pending: "neutral",
  active: "remote",
  paused: "caution",
  closed_listening: "remote",
  completed: "confirmed",
  terminated: "system",
}

const formatBubbleTime = (iso: string) => format(new Date(iso), "HH:mm")

const formatEnrolled = (iso: string | null) =>
  iso ? format(new Date(iso), "d MMM yyyy 'at' HH:mm") : "—"

const formatDayHeading = (iso: string) =>
  format(new Date(iso), "EEEE · d MMM yyyy")

const dayKey = (iso: string) => format(new Date(iso), "yyyy-MM-dd")

const mapMessageStatus = (
  status: string | null,
): "sent" | "read" | "failed" | undefined => {
  if (!status) return undefined
  if (status === "delivered" || status === "read") return "read"
  if (status === "failed" || status === "undelivered") return "failed"
  return "sent"
}

export default function AdminMsgFlowRunsShow({
  run,
  transcript,
  inspector,
}: Props) {
  const decorated = decorateBubbles(transcript, {
    closedListeningAt: run.closed_listening_at,
  })
  const groupedByDay = decorated.reduce<
    { day: string; lines: typeof decorated }[]
  >((acc, line) => {
    const day = dayKey(line.sent_at)
    const last = acc[acc.length - 1]
    if (last && last.day === day) {
      last.lines.push(line)
    } else {
      acc.push({ day, lines: [line] })
    }
    return acc
  }, [])
  const transcriptStatuses: Record<
    string,
    "sent" | "read" | "failed" | undefined
  > = {}
  transcript.forEach((line) => {
    transcriptStatuses[line.id] =
      line.kind === "outbound" ? mapMessageStatus(line.status) : undefined
  })

  return (
    <AdminLayout>
      <Head title={`MsgFlow Run · ${run.contact.name}`} />
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_440px] lg:gap-10">
          <section className="flex min-w-0 flex-col gap-8">
            <PageHeader
              crumbs={[
                { label: "message flows" },
                { label: "runs", href: adminMsgFlowRunsPath() },
                { label: run.contact.name },
              ]}
              code={`#${run.id}`}
              title={run.contact.name}
              facts={
                run.contact.account || run.contact.phone ? (
                  <>
                    {run.contact.account}
                    {run.contact.account && run.contact.phone ? " · " : ""}
                    {run.contact.phone}
                  </>
                ) : undefined
              }
              actions={
                <div className="lg:hidden">
                  <SlideoutMenu.Trigger>
                    <Button size="sm" color="secondary" iconLeading={Eye}>
                      Inspector
                    </Button>
                    <SlideoutMenu>
                      {({ close }) => (
                        <>
                          <SlideoutMenu.Header onClose={close}>
                            <h2 className="text-primary text-md font-semibold">
                              Inspector
                            </h2>
                          </SlideoutMenu.Header>
                          <SlideoutMenu.Content>
                            <RunInspector inspector={inspector} />
                          </SlideoutMenu.Content>
                        </>
                      )}
                    </SlideoutMenu>
                  </SlideoutMenu.Trigger>
                </div>
              }
            >
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <StatusBadge tier={STATUS_COLOR[run.status]}>
                  {STATUS_LABEL[run.status]}
                </StatusBadge>
                <Badge type="modern" color="gray" size="sm">
                  {run.definition.label}
                </Badge>
                <span className="notation">
                  enrolled {formatEnrolled(run.enrolled_at)}
                </span>
              </div>
            </PageHeader>

            <div className="flex flex-col gap-8">
              {groupedByDay.map(({ day, lines }) => (
                <section key={day} className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <span className="border-secondary h-px flex-1 border-t" />
                    <span className="eyebrow">
                      {formatDayHeading(lines[0].sent_at)}
                    </span>
                    <span className="border-secondary h-px flex-1 border-t" />
                  </div>
                  <ul className="flex flex-col gap-3">
                    {lines.map((line) => (
                      <MessageItem
                        key={line.id}
                        showUserLabel={false}
                        msg={{
                          id: line.id,
                          text: line.text,
                          sentAt: formatBubbleTime(line.sent_at),
                          status: transcriptStatuses[line.id],
                          user:
                            line.kind === "outbound"
                              ? { me: true, name: "batyr" }
                              : { me: false, name: run.contact.first_name },
                        }}
                      />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </section>

          <aside className="bg-primary hidden p-5 lg:sticky lg:top-8 lg:block lg:h-fit">
            <RunInspector inspector={inspector} />
          </aside>
        </div>
      </div>
    </AdminLayout>
  )
}
